import Button from 'components/common/button/Button';
import { ButtonColor } from 'components/common/button/Button.type';
import AlertModal from 'components/common/modal/alert/AlertModal';
import { CommonOptionType } from 'components/common/select/CommonSelectBox';
import { ICON } from 'constants/icons';
import { SERVER_DATE_FORMAT } from 'data/common';
import dayjs from 'dayjs';
import useStatistics from 'hooks/feature/stats/useStatistics';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CategoryUsage } from 'types/feature/statistics/categoryUsage';
import { convertDateToString } from 'utils/date/dateUtils';
import { handle400Error } from 'utils/error/handle400Error';
import CategoryBuildingFilter from './filter/building/CategoryBuildingFilter';
import CategoryRangeFilter from './filter/range/CategoryRangeFilter';
import CategoryWordCloud from './graph/CategoryWordCloud';
import CategoryUsageTable from './table/CategoryUsageTable';
import useStatsDownload from 'hooks/feature/stats/useStatsDownload';
import NoData from 'components/common/table/no-data/NoData';
import GradientLoader from 'components/common/loading/gradient/GradientLoader';

const CategoryUsageStatsContainer = () => {
  const [selectedPointOption, setSelectedPointOption] = useState<CommonOptionType>();

  // 실시간 데이터 지원하지 않음
  const yesterday = dayjs().subtract(1, 'day');

  const [selectedStart, setSelectedStart] = useState<Date>(yesterday.subtract(30, 'day').toDate());
  const [selectedEnd, setSelectedEnd] = useState<Date>(yesterday.toDate());

  const [categoryUsageList, setCategoryUsageList] = useState<CategoryUsage[]>([]);

  const [loading, setLoading] = useState(true);
  const [openAlert, setOpenAlert] = useState(false);

  const { getCategoryUsage } = useStatistics();
  const { downloadCategoryStats } = useStatsDownload();
  const { workspaceId } = useParams();

  // 인기 카테고리 조회 api 호출
  const refetchCategoryUsage = async (pointId?: string) => {
    if (workspaceId) {
      const response = await getCategoryUsage(
        workspaceId,
        pointId ?? selectedPointOption?.value,
        convertDateToString(selectedStart),
        convertDateToString(selectedEnd),
      );
      setCategoryUsageList(response);
    }
  };

  // 검색 버튼 클릭 시
  const onClickSearch = async () => {
    try {
      setLoading(true);

      // 인기 카테고리 조회 api 호출
      const response = await getCategoryUsage(
        workspaceId || '',
        selectedPointOption?.value,
        convertDateToString(selectedStart),
        convertDateToString(selectedEnd),
      );

      setCategoryUsageList(response);

      setLoading(false);
    } catch (error) {
      handle400Error(error, () => {
        setOpenAlert(true);
        setLoading(false);
      });
    }
  };

  // 기간 상태 초기화
  const refreshRangeState = () => {
    setSelectedStart(yesterday.subtract(30, 'day').toDate());
    setSelectedEnd(yesterday.toDate());
  };

  // 처음 세팅 상태로 api 호출
  const refreshMachineStats = async () => {
    try {
      setLoading(true);

      refreshRangeState();

      // 인기 카테고리 조회 api 호출
      const response = await getCategoryUsage(
        workspaceId || '',
        selectedPointOption?.value,
        yesterday.subtract(30, 'day').format(SERVER_DATE_FORMAT),
        yesterday.format(SERVER_DATE_FORMAT),
      );

      setCategoryUsageList(response);
    } catch (error) {
      handle400Error(error, () => {
        setOpenAlert(true);
      });
    } finally {
      setLoading(false);
    }
  };

  // 초기화 클릭 시 기간 초기화
  const onClickRefreshDates = async () => {
    refreshRangeState();

    // 처음 세팅 상태로 api 호출
    await refreshMachineStats();
  };

  // 인기 카테고리 다운로드
  const onClickDownload = async () => {
    await downloadCategoryStats({
      workspaceId: workspaceId || '',
      startDate: convertDateToString(selectedStart),
      endDate: convertDateToString(selectedEnd),
      pointId: selectedPointOption?.value,
    });
  };

  return (
    <div>
      <div className='flex flex-col gap-6'>
        {/* 빌딩 필터 */}
        <CategoryBuildingFilter
          selectedPointOption={selectedPointOption}
          setSelectedPointOption={setSelectedPointOption}
          refetchCategoryUsage={refetchCategoryUsage}
          setLoading={setLoading}
        />

        <div className='flex'>
          {/* 검색 기간 필터 */}
          <CategoryRangeFilter
            selectedStart={selectedStart}
            selectedEnd={selectedEnd}
            setSelectedStart={setSelectedStart}
            setSelectedEnd={setSelectedEnd}
          />

          <div className='flex gap-2.5'>
            {/* 검색 버튼 */}
            <Button onClick={onClickSearch} color={ButtonColor.primary} text='검색' size={80} />

            {/* 초기화 버튼 */}
            <Button
              onClick={onClickRefreshDates}
              color={ButtonColor.secondary}
              text='초기화'
              size={80}
              iconPath={ICON.REFRESH_BK}
            />
          </div>
        </div>

        {loading ? (
          <div className='w-full h-[404px] bg-white border border-gray-ea'>
            <GradientLoader />
          </div>
        ) : categoryUsageList.length === 0 ? (
          <>
            {/* 데이터 없을 경우 */}
            <NoData visible={categoryUsageList.length === 0} message='데이터가 없습니다.' />
          </>
        ) : (
          <>
            {/* 그래프 */}
            <CategoryWordCloud categoryUsageList={categoryUsageList} />

            {/* 테이블 */}
            <CategoryUsageTable
              categoryUsageList={categoryUsageList}
              isAll={selectedPointOption?.label === '전체'}
              onClickDownload={onClickDownload}
            />
          </>
        )}

        {openAlert && (
          <AlertModal
            message='통계 데이터를 불러올 수 없습니다.'
            closeModal={() => {
              setOpenAlert(false);
            }}
          />
        )}
      </div>
    </div>
  );
};
export default CategoryUsageStatsContainer;
